// Location: src/components/admin/AdminLayout.tsx
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { GaugeIcon } from '@/components/ui/icons/gauge-icon';
import { UsersIcon } from '@/components/ui/icons/users-icon';
import { ShareIcon } from '@/components/ui/icons/share-icon';
import { RouteIcon } from '@/components/ui/icons/route-icon';
import { LinkIcon } from '@/components/ui/icons/link-icon';
import { ConnectIcon } from '@/components/ui/icons/connect-icon';
import { UsersManagement } from './UsersManagement';
import { SocialProfiles } from './SocialProfiles';
import { ApiKeyManagement } from './ApiKeyManagement';
import { ProfileGroups } from './ProfileGroups';
import { LayersIcon } from '@/components/ui/icons/layers-icon';
import { FlameIcon } from '@/components/ui/icons/flame-icon';
import { AudioLinesIcon } from '@/components/ui/icons/audio-lines-icon';
import { AIManagement } from './AIManagement';
import { BrandVoice } from './BrandVoice';
import { ClockIcon } from '@/components/ui/icons/clock-icon';
import { SparklesIcon } from '@/components/ui/icons/sparkles-icon';
import { ContentMachine } from './ContentMachine';
import { ActivityIcon } from '@/components/ui/icons/activity-icon';
import { BuzzAdmin } from './BuzzAdmin';
import { ChartColumnIcon } from '@/components/ui/icons/chart-column-icon';
import { useAuth } from '@/hooks/useAuth';
import { UserMenu } from '@/components/ui/user-menu';
import { VersionPill } from '@/components/ui/version-pill';
import { FloatingVoiceButton } from '@/components/ui/floating-voice-button';
import { ClapIcon } from '@/components/ui/icons/clap-icon';
import { VideoContent } from './VideoContent';

export function AdminLayout({ children }: { children?: React.ReactNode }) {
	const location = useLocation();
	const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);
	const { isSuperAdmin, user } = useAuth();

	const navItems = [
		{
			title: 'Dashboard',
			href: '/admin',
			icon: (isHovered: boolean) => <GaugeIcon isHovered={isHovered} />,
		},
		{
			title: 'Social Profiles',
			href: '/admin/profiles',
			icon: (isHovered: boolean) => <ShareIcon isHovered={isHovered} />,
			component: SocialProfiles,
		},
		{
			title: 'Tribes',
			href: '/admin/groups',
			icon: (isHovered: boolean) => <LayersIcon isHovered={isHovered} />,
			component: ProfileGroups,
		},
		{
			title: 'Brand Voice',
			href: '/admin/voice',
			icon: (isHovered: boolean) => (
				<AudioLinesIcon isHovered={isHovered} />
			),
			component: BrandVoice,
		},
		{
			title: 'The Buzz',
			href: '/admin/buzz',
			icon: (isHovered: boolean) => (
				<ActivityIcon isHovered={isHovered} />
			),
			component: BuzzAdmin,
		},
		{
			title: 'Content Machine',
			href: '/admin/content-machine',
			icon: (isHovered: boolean) => (
				<SparklesIcon isHovered={isHovered} />
			),
			component: ContentMachine,
		},
		// {
		// 	title: 'Video Engine',
		// 	href: '/admin/video',
		// 	icon: (isHovered: boolean) => <ClapIcon isHovered={isHovered} />,
		// 	component: VideoContent,
		// },
		{
			title: 'Engagement Pods',
			href: '/admin/pods',
			icon: (isHovered: boolean) => <RouteIcon isHovered={isHovered} />,
		},

		{
			title: 'Schedules',
			href: '/admin/schedules',
			icon: (isHovered: boolean) => <ClockIcon isHovered={isHovered} />,
		},
	];

	return (
		<div className='flex h-screen'>
			<div className='orb-container'>
				<div className='orb orb-1'></div>
				<div className='orb orb-2'></div>
				<div className='orb orb-3'></div>
				<div className='orb orb-4'></div>
				<div className='orb orb-5'></div>
			</div>
			<div className='absolute inset-0 bg-white/30 dark:bg-black/40 z-0' />
			<div className='w-64 glass-morphism border-r !rounded-none relative z-10'>
				<nav className='p-4 flex flex-col h-full'>
					<Link
						to='/'
						className='flex flex-col items-center mb-6 hover:opacity-90 transition-all duration-300 cursor-pointer transform hover:scale-[1.02]'
					>
						<img
							src='/logo.png'
							alt='Prometheus Logo'
							className='w-60 h-auto'
						/>
						<span className='text-sm text-orange-500 -mt-1'>
							Stealing Fire From The Gods
						</span>
					</Link>
					<div className='space-y-2 flex-1'>
						{navItems.map((item) => (
							<Link
								key={item.href}
								to={item.href}
								className={cn(
									'flex items-center h-[55px] py-2 px-4 rounded-lg text-sm font-medium transition-colors',
									location.pathname === item.href
										? 'bg-primary text-primary-foreground'
										: 'hover:bg-muted'
								)}
								onMouseEnter={() => setHoveredItem(item.href)}
								onMouseLeave={() => setHoveredItem(null)}
							>
								{item.icon(hoveredItem === item.href)}
								<span className='ml-3'>{item.title}</span>
							</Link>
						))}
					</div>
				</nav>
			</div>
			<div className='flex-1 overflow-auto relative z-10'>
				<div className='p-6'>
					<UserMenu />
					{children || <Outlet />}
				</div>
			</div>
			<FloatingVoiceButton />
			<VersionPill />
		</div>
	);
}
